import type { ReactNode } from 'react';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { TSignupElement } from '@shared/dream-components';
import { SignupElement } from '@shared/dream-components';

import SignupAction from '~/components/SignupAction';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import usePricingDetails from '~/hooks/useLoaders/usePricingDetails';
import type { RootLoaderData } from '~/root';

import useGDPRTranslations from './hooks/useGDPRTranslations';

interface Props {
  node: TSignupElement;
  children: ReactNode | ReactNode[];
  premiumOfferId?: string;
}

const Signup = ({ node, children, premiumOfferId }: Props) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const [searchParams] = useSearchParams();
  const gdprTranslations = useGDPRTranslations();
  const premiumOfferIdFromUrl = searchParams.get('premium_offer_id') || undefined;
  const isSignupModal = searchParams.get('modal') === 'signup';
  const isPremiumOfferSignup = premiumOfferIdFromUrl && isSignupModal;
  const tierIdFromUrl = isPremiumOfferSignup ? searchParams.get('tier_id') : null;
  const { data: pricingDetails, isLoading: isTierLookupPending } = usePricingDetails({
    enabled: Boolean(tierIdFromUrl),
  });
  const tierRedirectUrl = pricingDetails?.tiers.find((tier) => tier.id === tierIdFromUrl)?.redirect_url;

  const isLoggedIn = useIsLoggedIn();
  const showWhenLoggedIn = node?.attrs?.showWhenLoggedIn;
  const hideForm = !showWhenLoggedIn && isLoggedIn && !isPremiumOfferSignup;

  if (hideForm) {
    return null;
  }

  return (
    <SignupAction
      id={node.attrs.id}
      signupFlowId={node?.attrs.signupFlowId}
      automationIds={node?.attrs.automationIds}
      premiumOfferId={premiumOfferId || premiumOfferIdFromUrl}
      redirectUrl={node?.attrs.redirectUrl}
      customFormType={node?.attrs.customFormType}
      isSubmitDisabled={isTierLookupPending}
      shouldSkipRedirectsAfterSuccess={Boolean(tierRedirectUrl)}
      shouldSkipAutoLogIn={Boolean(tierRedirectUrl)}
      onSuccess={
        tierRedirectUrl
          ? () => {
              window.location.href = tierRedirectUrl;
            }
          : undefined
      }
    >
      {({ isLoading, setEmail }) => (
        <SignupElement
          element={node}
          isSubmitting={isLoading || isTierLookupPending}
          isLive
          hasTermsOfService={!!site.viewable_site_version.settings.terms_of_service_enabled}
          hasCustomTermsOfService={site.viewable_site_version.settings.terms_of_service}
          customTermsOfServiceText={site.viewable_site_version.settings.terms_of_service_text || undefined}
          onEmailChange={setEmail}
          gdprTranslations={gdprTranslations}
        >
          {children}
        </SignupElement>
      )}
    </SignupAction>
  );
};

export default Signup;
